import { ScaleElement } from '@scale/base-element';

class ScaleTabs extends ScaleElement {
  static #resizes = new ResizeObserver((entries) => {
    for (const { target } of entries) target.closest('scale-tabs')?.placeIndicator();
  });

  #buttons = [];
  #panels = [];
  #activeIndex = 0;
  #nav = null;

  setup() {
    const nav = this.ref('tablist');
    const tabs = this.querySelectorAll('[data-ref="tab"]');
    if (!nav || !tabs.length) return;
    this.#nav = nav;

    for (const tab of tabs) {
      const template = tab.querySelector('[data-ref="tab-button-template"]');
      const panel = tab.querySelector('[role="tabpanel"]');
      if (!template || !panel) continue;

      const button = template.content.firstElementChild.cloneNode(true);
      button.setAttribute('aria-controls', panel.id || `tabpanel-${crypto.randomUUID().slice(0, 8)}`);
      if (!panel.id) panel.id = button.getAttribute('aria-controls');
      button.id = `tab-${panel.id}`;
      panel.setAttribute('aria-labelledby', button.id);

      nav.appendChild(button);
      this.#buttons.push(button);
      this.#panels.push(panel);

      this.on('click', () => this.#selectTab(button, { focus: true }), { target: button });
      this.on('keydown', (e) => this.#handleKeydown(e), { target: button });

      template.remove();
    }

    const indicator = document.createElement('span');
    indicator.className = 'block-tabs__indicator';
    indicator.setAttribute('aria-hidden', 'true');
    nav.appendChild(indicator);

    const prev = this.ref('prevArrow');
    const next = this.ref('nextArrow');
    if (prev) this.on('click', () => this.#navigate(-1), { target: prev });
    if (next) this.on('click', () => this.#navigate(1), { target: next });

    this.on('scroll', () => this.debounce('overflow', () => this.#markOverflow(), 60), { target: nav, passive: true });
    ScaleTabs.#resizes.observe(nav);
    for (const button of this.#buttons) ScaleTabs.#resizes.observe(button);

    if (this.#buttons.length) {
      this.#selectTab(this.#buttons[0], { focus: false });
      this.classList.add('is-initialized');
      requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('is-ready')));
    }
  }

  teardown() {
    if (this.#nav) ScaleTabs.#resizes.unobserve(this.#nav);
    for (const button of this.#buttons) ScaleTabs.#resizes.unobserve(button);
  }

  placeIndicator() {
    const button = this.#buttons[this.#activeIndex];
    if (!button || !this.#nav) return;
    const { style } = this.#nav;
    style.setProperty('--indicator-x', `${button.offsetLeft}px`);
    style.setProperty('--indicator-y', `${button.offsetTop}px`);
    style.setProperty('--indicator-w', `${button.offsetWidth}px`);
    style.setProperty('--indicator-h', `${button.offsetHeight}px`);
    this.#markOverflow();
  }

  #markOverflow() {
    const nav = this.#nav;
    const end = nav.scrollWidth - nav.clientWidth;
    const start = Math.abs(nav.scrollLeft);
    nav.classList.toggle('has-overflow-start', end > 1 && start > 1);
    nav.classList.toggle('has-overflow-end', end > 1 && start < end - 1);
  }

  #navigate(delta) {
    if (!this.#buttons.length) return;
    const count = this.#buttons.length;
    const next = (this.#activeIndex + delta + count) % count;
    this.#selectTab(this.#buttons[next], { focus: false });
  }

  #selectTab(button, { focus = true } = {}) {
    const index = this.#buttons.indexOf(button);
    if (index === -1) return;

    this.#activeIndex = index;

    for (let i = 0; i < this.#buttons.length; i++) {
      this.#buttons[i].setAttribute('aria-selected', i === index ? 'true' : 'false');
      this.#buttons[i].setAttribute('tabindex', i === index ? '0' : '-1');
      this.#panels[i].setAttribute('aria-hidden', i === index ? 'false' : 'true');
    }

    this.placeIndicator();
    const nav = this.#nav;
    const overflow = nav.scrollWidth - nav.clientWidth;
    if (overflow > 0) {
      const left = button.offsetLeft - (nav.clientWidth - button.offsetWidth) / 2;
      nav.scrollTo({ left: getComputedStyle(nav).direction === 'rtl' ? left - overflow : left });
    }

    if (focus) button.focus({ preventScroll: true });
  }

  #handleKeydown(e) {
    const index = this.#buttons.indexOf(e.currentTarget);
    let next;

    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault();
        next = this.#buttons[(index + 1) % this.#buttons.length];
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault();
        next = this.#buttons[(index - 1 + this.#buttons.length) % this.#buttons.length];
        break;
      case 'Home':
        e.preventDefault();
        next = this.#buttons[0];
        break;
      case 'End':
        e.preventDefault();
        next = this.#buttons[this.#buttons.length - 1];
        break;
      default:
        return;
    }

    if (next) this.#selectTab(next);
  }

  get activeIndex() {
    return this.#activeIndex;
  }

  onBlockSelect(e) {
    for (let i = 0; i < this.#panels.length; i++) {
      if (this.#panels[i].closest('[data-ref="tab"]')?.querySelector(`[id*="${e.detail.blockId}"]`)) {
        this.#selectTab(this.#buttons[i]);
        break;
      }
    }
  }
}

if (!customElements.get('scale-tabs')) {
  customElements.define('scale-tabs', ScaleTabs);
}
